import React, { useState, useCallback } from 'react';
import { User, Building, FileText, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ToastContainer, useToast } from '../../components/shared/Toast';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import { authService } from '../../services/authService';
import {
  ProfileInformationTab,
  BusinessProfileTab,
  TermsConditionsTab
} from '../../components/vendor/profile';

const VendorAccount = () => {
  const { user, updateUser, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);

  const deletionRequiresPassword = user?.deletion_requires_password !== false;

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDeletePassword('');
    setDeleteConfirmText('');
    setDeleteReason('');
  }, []);

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Confirmation required', 'Type DELETE (all caps) to confirm.');
      return;
    }
    if (deletionRequiresPassword && !deletePassword.trim()) {
      toast.error('Password required', 'Enter your account password.');
      return;
    }
    setDeleting(true);
    try {
      await authService.deleteAccount({
        ...(deletionRequiresPassword ? { password: deletePassword } : {}),
        reason: deleteReason.trim() || undefined,
      });
      toast.success('Account closed', 'Your account has been deleted.');
      localStorage.removeItem('token');
      await logout();
      navigate('/', { replace: true });
    } catch (err) {
      const data = err.response?.data;
      const msg =
        data?.message ||
        data?.errors?.password?.[0] ||
        err.message ||
        'Could not delete account.';
      toast.error('Cannot delete account', msg);
    } finally {
      setDeleting(false);
      closeDeleteModal();
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile Information', icon: User },
    { id: 'business', name: 'Business Profile', icon: Building },
    { id: 'terms', name: 'Terms & Conditions', icon: FileText }
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Account</h1>
          <p className="text-sm text-gray-600">Manage your account information and business profile</p>
        </div>
      </div>

      {/* Tab Navigation - Responsive */}
      <div className="border-b border-gray-200 mb-4 sm:mb-8 overflow-x-auto">
        <nav className="-mb-px flex space-x-2 sm:space-x-8 min-w-max sm:min-w-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 flex-shrink-0" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content - Responsive */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
        {activeTab === 'profile' && <ProfileInformationTab user={user} updateUser={updateUser} />}
        {activeTab === 'business' && <BusinessProfileTab user={user} updateUser={updateUser} />}
        {activeTab === 'terms' && <TermsConditionsTab user={user} updateUser={updateUser} />}
      </div>

      <div className="mt-6 sm:mt-8 bg-white rounded-lg shadow-sm border border-red-200 p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Danger zone</h2>
            <p className="mt-1 text-sm text-gray-600">
              Permanently close your vendor account. You cannot undo this. Active bookings may block
              deletion until they are completed or cancelled.
            </p>
            <div className="mt-4">
              <Button
                type="button"
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setDeleteModalOpen(true)}
              >
                Delete account
              </Button>
            </div>
          </div>
        </div>
      </div>

      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => !deleting && closeDeleteModal()}
              aria-hidden="true"
            />
            <div
              className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 border border-gray-200"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-account-title"
            >
              <h2 id="delete-account-title" className="text-lg font-semibold text-gray-900 mb-2">
                Delete your account
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {deletionRequiresPassword ? (
                  <>
                    This will close your account and sign you out. Enter your password and type{' '}
                    <span className="font-mono font-semibold">DELETE</span> to confirm.
                  </>
                ) : (
                  <>
                    You signed in without a password (for example with Google). Type{' '}
                    <span className="font-mono font-semibold">DELETE</span> to confirm you want to close this account.
                  </>
                )}
              </p>
              <form onSubmit={handleDeleteAccount} className="space-y-4">
                {deletionRequiresPassword && (
                  <div>
                    <label htmlFor="delete-account-password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <Input
                      id="delete-account-password"
                      type="password"
                      autoComplete="current-password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      disabled={deleting}
                      placeholder="Your account password"
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="delete-account-reason" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <Input
                    id="delete-account-reason"
                    type="text"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    disabled={deleting}
                    placeholder="Tell us why you are leaving"
                  />
                </div>
                <div>
                  <label htmlFor="delete-account-confirm" className="block text-sm font-medium text-gray-700 mb-1">
                    Type DELETE to confirm
                  </label>
                  <Input
                    id="delete-account-confirm"
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    disabled={deleting}
                    placeholder="DELETE"
                    autoComplete="off"
                  />
                </div>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={closeDeleteModal} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="danger" disabled={deleting} loading={deleting}>
                    Delete my account
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer 
        toasts={toast.toasts} 
        position="top-right"
        onRemove={toast.removeToast}
      />
    </>
  );
};

export default VendorAccount;

